import React, { useState } from 'react';
import { PlusIcon, Trash2Icon } from 'lucide-react';
import { LeadSource } from '../../types';
import { createSource, deleteSource, updateSource } from '../../services/sourceService';
import { Drawer } from '../ui/Drawer';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Switch } from '../ui/Switch';
import { ConfirmDialog } from './ConfirmDialog';
import { STAGE_COLORS } from './StageManager';
import { SOURCE_ICON_OPTIONS, sourceIcon } from './SourceBadge';
interface SourceManagerProps {
  open: boolean;
  onClose: () => void;
  organizationId: string;
  sources: LeadSource[];
}

/** Gestión de las fuentes de captación de la organización. */
export function SourceManager({
  open,
  onClose,
  organizationId,
  sources
}: SourceManagerProps) {
  const [name, setName] = useState('');
  const [icon, setIcon] = useState(SOURCE_ICON_OPTIONS[0].value);
  const [color, setColor] = useState(STAGE_COLORS[1].value);
  const [creating, setCreating] = useState(false);
  const [confirm, setConfirm] = useState<LeadSource | null>(null);
  async function handleCreate() {
    if (!name.trim()) return;
    setCreating(true);
    await createSource(organizationId, name.trim(), icon, color);
    setName('');
    setCreating(false);
  }
  return <>
      <Drawer open={open} onClose={onClose} title="Gestionar fuentes" data-id="element-222" data-mp="z37mkjufjy3z">
        <p className="mb-5 text-sm text-muted-foreground" data-id="element-223" data-mp="bdwstmbhz3zc">
          La fuente se detecta automáticamente cuando es posible. Aquí puedes
          renombrarla, cambiar su icono y su color, o desactivarla.
        </p>

        <ul className="space-y-3" data-id="element-224" data-mp="k6qb7sm8qjhf">
          {sources.map(source => {
          const Icon = sourceIcon(source.icon);
          return <li key={source.id} className="rounded-xl border border-border bg-background p-3" data-id="element-225" data-mp="fcm499cz6bgc">
                <div className="flex items-center gap-2" data-id="element-226" data-mp="3nrw2g6dquwiu">
                  <span className="flex h-9 w-9 flex-none items-center justify-center rounded-lg" style={{
                backgroundColor: `${source.color}1A`
              }} data-id="element-227" data-mp="3jrvp6p2dsrdd">
                    <Icon className="h-4 w-4" style={{
                  color: source.color
                }} aria-hidden="true" data-id="element-228" data-mp="1rqc74kslk78s" />
                  </span>
                  <Input id={`source-${source.id}`} aria-label="Nombre de la fuente" value={source.name} onChange={e => updateSource(source.id, {
                name: e.target.value
              })} data-id="element-229" data-mp="1wjx5idy3r38i" />
                  <button type="button" aria-label="Eliminar fuente" onClick={() => setConfirm(source)} className="rounded-md p-2 text-muted-foreground hover:bg-surface hover:text-red-600" data-id="element-230" data-mp="13vj8o1ir2cyj">
                    <Trash2Icon className="h-4 w-4" data-id="element-231" data-mp="1dtfk6ufln9x6" />
                  </button>
                </div>
                <div className="mt-3 grid gap-3 sm:grid-cols-2" data-id="element-232" data-mp="1iocpprdo715b">
                  <Select id={`source-icon-${source.id}`} label="Icono" options={SOURCE_ICON_OPTIONS} value={source.icon} onChange={e => updateSource(source.id, {
                icon: e.target.value
              })} data-id="element-233" data-mp="2p5q1a3kzdhls" />
                  <Select id={`source-color-${source.id}`} label="Color" options={STAGE_COLORS.map(c => ({
                value: c.value,
                label: c.label
              }))} value={source.color} onChange={e => updateSource(source.id, {
                color: e.target.value
              })} data-id="element-234" data-mp="ihfxanjjkjvq" />
                </div>
                <div className="mt-3" data-id="element-235" data-mp="3uznzrvcu9b8a">
                  <Switch id={`source-active-${source.id}`} label="Activa" checked={source.active} onChange={active => updateSource(source.id, {
                active
              })} data-id="element-236" data-mp="3v0p83s2gzjmb" />
                </div>
              </li>;
        })}
        </ul>

        <div className="mt-6 rounded-xl border border-dashed border-border p-4" data-id="element-237" data-mp="3m0sq29al5j08">
          <Input id="new-source" label="Nueva fuente" placeholder="Ejemplo: LinkedIn Ads" value={name} onChange={e => setName(e.target.value)} data-id="element-238" data-mp="3h1poiyttdux0" />
          <div className="mt-3 grid gap-3 sm:grid-cols-2" data-id="element-239" data-mp="1f80566larusn">
            <Select id="new-source-icon" label="Icono" options={SOURCE_ICON_OPTIONS} value={icon} onChange={e => setIcon(e.target.value)} data-id="element-240" data-mp="1f6f5y3o1fnog" />
            <Select id="new-source-color" label="Color" options={STAGE_COLORS.map(c => ({
            value: c.value,
            label: c.label
          }))} value={color} onChange={e => setColor(e.target.value)} data-id="element-241" data-mp="gx781qsx6bjy" />
          </div>
          <Button className="mt-4 w-full" leftIcon={<PlusIcon className="h-4 w-4" data-id="element-243" data-mp="3pcik4lqfq2i0" />} loading={creating} disabled={!name.trim()} onClick={handleCreate} data-id="element-242" data-mp="7ufu9kckn3p5">
            Crear fuente
          </Button>
        </div>
      </Drawer>

      <ConfirmDialog open={Boolean(confirm)} title={`Eliminar «${confirm?.name ?? ''}»`} description="Los leads que la tuvieran asignada seguirán mostrando su fuente original, pero dejará de estar disponible." onCancel={() => setConfirm(null)} onConfirm={() => {
      if (confirm) deleteSource(confirm.id);
      setConfirm(null);
    }} data-id="element-244" data-mp="3u5bqfe1jxiw7" />
    </>;
}